import { Group, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts';
import { type VizAccent } from '@/design/VizAccents';
import { FLUENCY_AXES, type FluencyAxisScore } from './InsightsTypes';

interface InsightsRadarChartProps {
    user: FluencyAxisScore[];
    /** Optional cohort axis averages, parallel to FLUENCY_AXES. Omit for a user-only radar. */
    cohort?: number[];
    cohortLabel?: string;
    /** @deprecated The "You" series is brand-coloured now; accent is ignored. */
    accent?: VizAccent;
}

interface RadarRow {
    axis: string;
    user: number;
    cohort: number;
}

function buildRows(user: FluencyAxisScore[], cohort: number[] | undefined): RadarRow[] {
    return FLUENCY_AXES.map((axis, i) => ({
        axis,
        user: user.find((s) => s.axis === axis)?.user ?? 0,
        cohort: cohort?.[i] ?? 0,
    }));
}

/** Fluency radar: the user's five axis scores, optionally overlaid on a cohort average. */
export default function InsightsRadarChart({ user, cohort, cohortLabel }: InsightsRadarChartProps): ReactElement {
    // "You" always reads in the brand accent; the cohort overlay stays neutral.
    const tokens = { solid: 'var(--bm-color-brand-accent)' };
    const hasCohort = cohort !== undefined && cohort.some((v) => v > 0);
    const rows = buildRows(user, cohort);
    return (
        <div>
            <ResponsiveContainer width="100%" height={264}>
                <RadarChart data={rows} outerRadius="76%">
                    <PolarGrid stroke="var(--bm-color-border-subtle)" />
                    <PolarAngleAxis dataKey="axis" tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    {hasCohort && (
                        <Radar name={cohortLabel ?? 'Cohort'} dataKey="cohort" stroke="var(--bm-color-text-dimmed)" strokeDasharray="4 4" fill="var(--bm-color-text-dimmed)" fillOpacity={0.08} />
                    )}
                    <Radar name="You" dataKey="user" stroke={tokens.solid} fill={tokens.solid} fillOpacity={0.32} />
                </RadarChart>
            </ResponsiveContainer>
            <Group gap="lg" justify="center" mt={4}>
                <Group gap={6}>
                    <span style={{ width: 10, height: 10, background: tokens.solid, borderRadius: 2, display: 'inline-block' }} />
                    <Text size="xs" c="dimmed">
                        You
                    </Text>
                </Group>
                {hasCohort && (
                    <Group gap={6}>
                        <span style={{ width: 10, height: 2, background: 'var(--bm-color-text-dimmed)', display: 'inline-block' }} />
                        <Text size="xs" c="dimmed">
                            {cohortLabel ?? 'Cohort'}
                        </Text>
                    </Group>
                )}
            </Group>
        </div>
    );
}
